<template>
  <div class="owner-program bg-u-px-md">
    <div class="flex justify-space-between align-center">
      <bg-text size="heading-2">Program Pemilik</bg-text>

      <bg-button-icon
        v-if="hasManagePermission"
        icon="add-plus"
        size="lg"
        variant="secondary"
        @click="goToCreateOwnerProgram"
      >
        Buat Program
      </bg-button-icon>
    </div>

    <bg-table :columns="columns" :rows="rows" :loading="isLoading">
      <template #column-status="{ row }">
        <bg-label :color="row.status.tagColor">{{ row.status.label }}</bg-label>

        <bg-text
          v-if="row.status.key === 'scheduled'"
          size="label-4"
          class="bg-u-mt-xxs bg-u-text-neutral-700"
        >
          <em>Di-publish {{ row.published_at }}</em>
        </bg-text>
      </template>

      <template #column-action="{ row }">
        <bg-dropdown
          v-if="getActions(row.status.key).length"
          menu-placement="bottom-end"
        >
          <template #trigger>
            <bg-icon name="more-vertical" class="cursor-pointer" />
          </template>

          <bg-dropdown-item
            v-for="action in getActions(row.status.key)"
            :key="action.key"
            @click="handleAction(action.key, row)"
          >
            <div class="flex align-center">
              <bg-icon :name="action.icon" size="sm" />
              <bg-text size="body-2">{{ action.label }}</bg-text>
            </div>
          </bg-dropdown-item>
        </bg-dropdown>
      </template>

      <template #empty-state>
        <bg-text size="title-5">Belum Ada Program Pemilik</bg-text>
        <bg-text size="body-2">
          Klik "Buat Program" untuk membuat program pemilik
        </bg-text>
      </template>
    </bg-table>

    <bg-pagination
      v-if="totalPage >= 1"
      :value="currentPage"
      :page-total="totalPage"
      class="bg-u-mt-xl"
      @click="changePage"
    />

    <bg-modal
      :value="confirmModal.isOpen"
      :title="`Yakin ingin ${confirmModal.actionLabel.toLowerCase()} program
      &quot;${confirmModal.programName}&quot; sekarang?`"
      :button-main-text="`${confirmModal.actionLabel} Program`"
      button-second-text="Batal"
      :button-main-props="{
        loading: confirmModal.isLoading,
      }"
      @close="closeConfirmModal"
      @click-main-action="confirmModalAction"
      @click-second-action="closeConfirmModal"
    >
      <bg-text size="body-2" v-html="confirmModal.description" />
    </bg-modal>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  getCurrentInstance,
} from '@vue/composition-api';

import {
  BgText,
  BgIcon,
  BgButtonIcon,
  BgTable,
  BgLabel,
  BgDropdown,
  BgDropdownItem,
  BgPagination,
  BgModal,
} from 'bangul-vue';

import ownerProgramApi from '@admin_endpoints/owner-program';

import { PROGRAM_TARGET_LABEL, STATUS_LABEL } from './constants';

import { dateFormatterToDisplay } from '@/utils/formatter';

const STATUS_MAP = {
  draft: { label: STATUS_LABEL.draft, tagColor: 'white' },
  scheduled: { label: STATUS_LABEL.scheduled, tagColor: 'blue' },
  active: { label: STATUS_LABEL.active, tagColor: 'green' },
  ended: { label: STATUS_LABEL.ended, tagColor: 'grey' },
};

const OWNER_WEB_BASE_URL =
  process.env.VUE_APP_OAUTH2_DOMAIN_OWNER || 'https://owner.singgahsini.id';

const buildProgramShareLink = id =>
  `${OWNER_WEB_BASE_URL}/owner/owner-program?highlight_program_id=${id}`;

const ACTION_CONFIG = {
  edit: {
    key: 'edit',
    label: 'Ubah Program',
    icon: 'edit',
    requiresPermission: true,
  },
  result: {
    key: 'result',
    label: 'Lihat Result',
    icon: 'visible',
  },
  copyLink: {
    key: 'copyLink',
    label: 'Salin Link Program',
    icon: 'copy',
  },
  stop: {
    key: 'stop',
    label: 'Akhiri Program',
    icon: 'close',
    requiresPermission: true,
  },
  delete: {
    key: 'delete',
    label: 'Hapus Program',
    icon: 'delete',
    requiresPermission: true,
  },
};

const STATUS_ACTIONS = {
  draft: ['edit', 'delete'],
  scheduled: ['edit', 'delete'],
  active: ['edit', 'result', 'copyLink', 'stop', 'delete'],
  ended: ['result', 'delete'],
};

const mapProgramToRow = program => ({
  id: program.id,
  name: program.name,
  status: {
    key: program.status,
    ...STATUS_MAP[program.status],
  },
  program_target:
    PROGRAM_TARGET_LABEL[program.target_type] || program.target_type || '-',
  program_period: `${dateFormatterToDisplay(
    program.start_date,
    'DD/MM/YYYY'
  )} - ${dateFormatterToDisplay(program.end_date, 'DD/MM/YYYY')}`,
  published_at: dateFormatterToDisplay(program.published_at, 'DD MMM YYYY'),
  updated_at: dateFormatterToDisplay(program.updated_at, 'DD/MM/YYYY, HH:mm'),
});

const ITEMS_PER_PAGE = 10;

export default {
  name: 'OwnerProgram',

  components: {
    BgText,
    BgIcon,
    BgButtonIcon,
    BgTable,
    BgLabel,
    BgDropdown,
    BgDropdownItem,
    BgPagination,
    BgModal,
  },

  setup() {
    const { $router, $toast, $error, $store } = getCurrentInstance().proxy;

    const hasManagePermission = computed(() =>
      $store.getters.xCheckUserPermission('manage-program-owner')
    );

    const columns = [
      { label: 'Nama Program', key: 'name' },
      { label: 'Status', key: 'status' },
      { label: 'Target Program', key: 'program_target' },
      { label: 'Periode Program', key: 'program_period' },
      { label: 'Last Update', key: 'updated_at' },
      { label: 'Action', key: 'action' },
    ];

    const rows = ref([]);
    const isLoading = ref(false);
    const currentPage = ref(1);
    const totalItems = ref(0);

    const totalPage = computed(() =>
      Math.ceil(totalItems.value / ITEMS_PER_PAGE)
    );

    const fetchOwnerPrograms = async () => {
      isLoading.value = true;

      try {
        const offset = (currentPage.value - 1) * ITEMS_PER_PAGE;

        const response = await ownerProgramApi.getOwnerPrograms({
          offset,
          limit: ITEMS_PER_PAGE,
        });

        const programs = response?.data?.program_owners || [];

        totalItems.value = response?.data?.metadata?.total || 0;
        rows.value = programs.map(mapProgramToRow);
      } catch (error) {
        $error?.report(error);
        $toast.show('Gagal memuat program pemilik. Silakan coba lagi.', {
          variant: 'error',
        });
      } finally {
        isLoading.value = false;
      }
    };

    const changePage = page => {
      currentPage.value = page;
      fetchOwnerPrograms();
    };

    const confirmModal = ref({
      isOpen: false,
      isLoading: false,
      programName: '',
      actionLabel: '',
      description: '',
      onConfirm: null,
    });

    const closeConfirmModal = () => {
      confirmModal.value.isOpen = false;
      confirmModal.value.onConfirm = null;
    };

    const confirmModalAction = async () => {
      if (!confirmModal.value.onConfirm) return;

      confirmModal.value.isLoading = true;

      try {
        await confirmModal.value.onConfirm();

        closeConfirmModal();
      } finally {
        confirmModal.value.isLoading = false;
      }
    };

    const stopOwnerProgram = row => {
      confirmModal.value.programName = row.name;
      confirmModal.value.actionLabel = 'Akhiri';
      confirmModal.value.description =
        'Dengan akhiri program, status akan berubah menjadi <b>Berakhir</b> dan program tidak akan tampil lagi di Dashboard Singgahsini pemilik.';

      confirmModal.value.onConfirm = async () => {
        try {
          await ownerProgramApi.stopOwnerProgram(row.id);
          $toast.show('Program berhasil diakhiri.');
          fetchOwnerPrograms();
        } catch (error) {
          $error?.report(error);
          $toast.show('Gagal mengakhiri program. Silakan coba lagi.', {
            variant: 'error',
          });
        }
      };

      confirmModal.value.isOpen = true;
    };

    const deleteOwnerProgram = row => {
      confirmModal.value.programName = row.name;
      confirmModal.value.actionLabel = 'Hapus';
      confirmModal.value.description =
        'Seluruh informasi program akan terhapus dan tidak dapat dikembalikan.';

      confirmModal.value.onConfirm = async () => {
        try {
          await ownerProgramApi.deleteOwnerProgram(row.id);
          $toast.show('Program berhasil dihapus.');

          if (rows.value.length === 1 && currentPage.value > 1) {
            currentPage.value -= 1;
          }

          fetchOwnerPrograms();
        } catch (error) {
          $error?.report(error);
          $toast.show('Gagal menghapus program. Silakan coba lagi.', {
            variant: 'error',
          });
        }
      };

      confirmModal.value.isOpen = true;
    };

    const getActions = statusKey => {
      const keys = STATUS_ACTIONS[statusKey] || [];

      return keys
        .map(key => ACTION_CONFIG[key])
        .filter(
          action => !action.requiresPermission || hasManagePermission.value
        );
    };

    const copyProgramLink = async id => {
      try {
        await navigator.clipboard.writeText(buildProgramShareLink(id));
        $toast.show('Link program berhasil disalin.');
      } catch (error) {
        $error?.report(error);
        $toast.show('Gagal menyalin link program. Silakan coba lagi.', {
          variant: 'error',
        });
      }
    };

    const handleAction = (actionKey, row) => {
      switch (actionKey) {
        case 'edit':
          $router.push({
            name: 'owner-program-detail',
            params: { id: row.id },
          });
          break;
        case 'result':
          $router.push({
            name: 'owner-program-detail',
            params: { id: row.id },
            query: { tab: 'result' },
          });
          break;
        case 'copyLink':
          copyProgramLink(row.id);
          break;
        case 'stop':
          stopOwnerProgram(row);
          break;
        case 'delete':
          deleteOwnerProgram(row);
          break;
        default:
          break;
      }
    };

    const goToCreateOwnerProgram = () => {
      $router.push({ name: 'owner-program-create' });
    };

    onMounted(() => {
      fetchOwnerPrograms();
    });

    return {
      hasManagePermission,

      columns,
      rows,
      isLoading,
      currentPage,
      totalPage,

      confirmModal,
      closeConfirmModal,
      confirmModalAction,

      getActions,
      handleAction,
      changePage,
      goToCreateOwnerProgram,
    };
  },
};
</script>

<style lang="scss" scoped src="./OwnerProgram.scss" />
